import { motion } from "framer-motion";
import SectionHead from "../SectionHead";

const IMG =
    "https://images.unsplash.com/photo-1717386255773-1e3037c81788?q=80&w=1400&auto=format&fit=crop";

const PRESS_ROWS = [
    { equipment: "Hydraulic / Compression Press", capacity: "on_request", qty: "on_request" },
    { equipment: "Hydraulic / Compression Press", capacity: "on_request", qty: "on_request" },
    { equipment: "Heating System", capacity: "on_request", qty: "on_request" },
];

const MACHINING_ROWS = [
    { machine: "Drilling Machine", make: "on_request", capacity: "on_request", qty: "on_request" },
    { machine: "Milling / Machining Machine", make: "on_request", capacity: "on_request", qty: "on_request" },
    { machine: "Cutting Machine", make: "on_request", capacity: "on_request", qty: "on_request" },
    { machine: "Other Support Equipment", make: "on_request", capacity: "on_request", qty: "on_request" },
];

const Req = () => <span className="on-request">On request</span>;

const CUTTING_CHIPS = [
    "Sheet cutting",
    "Precision cutting",
    "Edge trimming",
    "Profile cutting",
    "Component preparation",
];

const DRILLING_CHIPS = [
    "Drilling",
    "Hole machining",
    "Slotting",
    "Trimming",
    "Surface finishing",
    "Dimensional correction",
];

const Block = ({ tag, title, desc, children }) => (
    <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.6 }}
        className="border border-zinc-800 bg-[#0e0e11]"
    >
        <div className="p-6 md:p-8">
            <div className="flex items-center gap-3 mb-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-safety border border-safety/40 px-2.5 py-1">
                    {tag}
                </span>
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-semibold uppercase tracking-tight text-zinc-50">
                {title}
            </h3>
            <p className="text-zinc-400 text-sm leading-relaxed mt-3">{desc}</p>
        </div>
        <div className="border-t border-zinc-800">{children}</div>
    </motion.div>
);

export const Machinery = () => (
    <section id="machinery" className="py-24 md:py-32 scroll-mt-20 bg-panel/40 border-y border-zinc-900">
        <div className="shell">
            <SectionHead
                num="04"
                overline="Machinery & Installed Technology"
                title="Machines behind the spec"
                desc="Compression pressing, fabrication and machining equipment installed for controlled FRP and epoxy component production. Detailed machine specifications and capacities are shared on request."
            />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
                <div className="lg:col-span-7 space-y-8">
                    <Block
                        tag="A"
                        title="Compression / Hydraulic Pressing"
                        desc="Used for manufacturing FRP and epoxy-based components through controlled moulding and pressing processes."
                    >
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse min-w-[480px]">
                                <thead>
                                    <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-zinc-800">
                                        <th className="py-3 px-6 font-normal">Equipment</th>
                                        <th className="py-3 px-6 font-normal">Capacity</th>
                                        <th className="py-3 px-6 font-normal">Qty.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {PRESS_ROWS.map((r, i) => (
                                        <tr
                                            key={`${r.equipment}-${i}`}
                                            data-testid={`machinery-press-row-${i + 1}`}
                                            className="border-b border-zinc-900 hover:bg-panel transition-colors"
                                        >
                                            <td className="py-4 px-6 text-sm text-zinc-200">{r.equipment}</td>
                                            <td className="py-4 px-6">{r.capacity === "on_request" ? <Req /> : r.capacity}</td>
                                            <td className="py-4 px-6">{r.qty === "on_request" ? <Req /> : r.qty}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Block>

                    <Block
                        tag="C"
                        title="Drilling & Machining"
                        desc="Capability for drawing-based FRP components."
                    >
                        <div className="p-6 md:px-8">
                            <div className="flex flex-wrap gap-2 mb-6">
                                {DRILLING_CHIPS.map((c) => (
                                    <span key={c} className="chip">{c}</span>
                                ))}
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse min-w-[520px]">
                                    <thead>
                                        <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-zinc-800">
                                            <th className="py-3 pr-6 font-normal">Machine</th>
                                            <th className="py-3 px-4 font-normal">Make / Model</th>
                                            <th className="py-3 px-4 font-normal">Capacity</th>
                                            <th className="py-3 px-4 font-normal">Qty.</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {MACHINING_ROWS.map((r, i) => (
                                            <tr
                                                key={r.machine}
                                                data-testid={`machinery-machining-row-${i + 1}`}
                                                className="border-b border-zinc-900 hover:bg-panel transition-colors"
                                            >
                                                <td className="py-4 pr-6 text-sm text-zinc-200 whitespace-nowrap">{r.machine}</td>
                                                <td className="py-4 px-4">{r.make === "on_request" ? <Req /> : r.make}</td>
                                                <td className="py-4 px-4">{r.capacity === "on_request" ? <Req /> : r.capacity}</td>
                                                <td className="py-4 px-4">{r.qty === "on_request" ? <Req /> : r.qty}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </Block>
                </div>

                <div className="lg:col-span-5 space-y-8">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="relative border border-zinc-800 group overflow-hidden h-[300px] lg:h-[380px]"
                    >
                        <img
                            src={IMG}
                            alt="Installed machinery"
                            className="absolute inset-0 w-full h-full object-cover grayscale-[35%] group-hover:grayscale-0 transition-all duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-transparent to-transparent" />
                        <div className="absolute bottom-0 inset-x-0 p-6">
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-safety">
                                Installed Technology
                            </div>
                            <div className="font-display uppercase text-xl text-zinc-100 mt-1">
                                Pressing • Fabrication • Machining
                            </div>
                        </div>
                    </motion.div>

                    <Block
                        tag="B"
                        title="Cutting & Fabrication"
                        desc="Our fabrication setup supports preparation and finishing of FRP/GFRP materials."
                    >
                        <div className="p-6 md:px-8">
                            <div className="flex flex-wrap gap-2">
                                {CUTTING_CHIPS.map((c) => (
                                    <span key={c} className="chip">{c}</span>
                                ))}
                            </div>
                            <p className="mt-6 text-xs text-zinc-500 font-mono uppercase tracking-[0.15em]">
                                Machine make / capacity — shared on request
                            </p>
                        </div>
                    </Block>
                </div>
            </div>
        </div>
    </section>
);

export default Machinery;